import React, { useState, useEffect } from 'react';
import { Phone, PhoneOff, Video, X } from 'lucide-react';
import api from '../services/api';

interface IncomingCall {
  callId: number;
  roomName: string;
  callType: 'audio' | 'video' | 'screen';
  isGroupCall: boolean;
  initiator: {
    id: number;
    username: string;
  };
}

interface CallNotificationProps {
  onAccept: (call: IncomingCall) => void;
  onReject: (callId: number) => void;
}

const CallNotification: React.FC<CallNotificationProps> = ({ onAccept, onReject }) => {
  const [incomingCalls, setIncomingCalls] = useState<IncomingCall[]>([]);

  useEffect(() => {
    // Listen for incoming call events from SSE
    const handleIncomingCall = (event: any) => {
      const callData = event.detail;

      // Add to incoming calls list
      setIncomingCalls((prev) => {
        // Check if call already exists
        if (prev.some((call) => call.callId === callData.callId)) {
          return prev;
        }
        return [...prev, callData];
      });

      // Play ringtone
      playRingtone();

      // Set timeout to mark as missed after 60 seconds
      const timeoutId = setTimeout(async () => {
        try {
          await api.post(`/calls/${callData.callId}/missed`);
          setIncomingCalls((prev) => prev.filter((call) => call.callId !== callData.callId));
          stopRingtone();
        } catch (error) {
          console.error('Error marking call as missed:', error);
        }
      }, 60000); // 60 seconds

      // Store timeout ID for cleanup
      (window as any)[`callTimeout_${callData.callId}`] = timeoutId;
    };

    const handleCallEnded = (event: any) => {
      const { callId } = event.detail;

      // Clear timeout for this call
      const timeoutId = (window as any)[`callTimeout_${callId}`];
      if (timeoutId) {
        clearTimeout(timeoutId);
        delete (window as any)[`callTimeout_${callId}`];
      }

      setIncomingCalls((prev) => prev.filter((call) => call.callId !== callId));
      stopRingtone();
    };

    const handleCallRejected = (event: any) => {
      const { callId } = event.detail;

      // Clear timeout for this call
      const timeoutId = (window as any)[`callTimeout_${callId}`];
      if (timeoutId) {
        clearTimeout(timeoutId);
        delete (window as any)[`callTimeout_${callId}`];
      }

      setIncomingCalls((prev) => prev.filter((call) => call.callId !== callId));
      stopRingtone();
    };

    window.addEventListener('incoming_call', handleIncomingCall as EventListener);
    window.addEventListener('call_ended', handleCallEnded as EventListener);
    window.addEventListener('call_rejected', handleCallRejected as EventListener);

    return () => {
      window.removeEventListener('incoming_call', handleIncomingCall as EventListener);
      window.removeEventListener('call_ended', handleCallEnded as EventListener);
      window.removeEventListener('call_rejected', handleCallRejected as EventListener);
      stopRingtone();
    };
  }, []);

  let ringtoneAudio: HTMLAudioElement | null = null;

  const playRingtone = () => {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 440; // A4 note
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);

      // Repeat every 2 seconds
      const interval = setInterval(() => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain);
        gain.connect(audioContext.destination);
        osc.frequency.value = 440;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.3, audioContext.currentTime);
        osc.start(audioContext.currentTime);
        osc.stop(audioContext.currentTime + 0.2);
      }, 2000);

      (window as any).callRingtoneInterval = interval;
    } catch (error) {
      console.error('Error playing ringtone:', error);
    }
  };

  const stopRingtone = () => {
    if ((window as any).callRingtoneInterval) {
      clearInterval((window as any).callRingtoneInterval);
      (window as any).callRingtoneInterval = null;
    }
  };

  const handleAccept = async (call: IncomingCall) => {
    try {
      // Clear timeout for this call
      const timeoutId = (window as any)[`callTimeout_${call.callId}`];
      if (timeoutId) {
        clearTimeout(timeoutId);
        delete (window as any)[`callTimeout_${call.callId}`];
      }

      await api.post(`/calls/${call.callId}/answer`);
      setIncomingCalls((prev) => prev.filter((c) => c.callId !== call.callId));
      stopRingtone();
      onAccept(call);
    } catch (error) {
      console.error('Error accepting call:', error);
    }
  };

  const handleReject = async (callId: number) => {
    try {
      // Clear timeout for this call
      const timeoutId = (window as any)[`callTimeout_${callId}`];
      if (timeoutId) {
        clearTimeout(timeoutId);
        delete (window as any)[`callTimeout_${callId}`];
      }

      await api.post(`/calls/${callId}/reject`);
      setIncomingCalls((prev) => prev.filter((call) => call.callId !== callId));
      stopRingtone();
      onReject(callId);
    } catch (error) {
      console.error('Error rejecting call:', error);
    }
  };

  if (incomingCalls.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-4">
      {incomingCalls.map((call) => (
        <div
          key={call.callId}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-6 w-96 border-2 border-primary animate-pulse"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                {call.callType === 'video' ? (
                  <Video className="w-6 h-6 text-white" />
                ) : (
                  <Phone className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {call.isGroupCall ? 'Grup Görüşmesi' : 'Gelen Arama'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {call.initiator.username}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => handleAccept(call)}
              className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              <Phone className="w-5 h-5" />
              Kabul Et
            </button>
            <button
              onClick={() => handleReject(call.callId)}
              className="flex-1 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              <PhoneOff className="w-5 h-5" />
              Reddet
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CallNotification;
